/**
 * PublisherNotesFeed — chronological Substack-style timeline of every
 * recent Publisher note across all authors.
 *
 * Route: /publisher/notes (canonical on blog.socialpulse.com)
 * Data:  GET /api/publisher/v1/notes/timeline
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, StickyNote, ArrowUpRight, MapPin, User, Plus, X, Send, LogIn } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.round(d / 30);
  return `${mo}mo ago`;
}

export default function PublisherNotesFeed() {
  const [notes, setNotes] = useState(null); // null=loading, []=empty, [...]=ready
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const stopRef = useRef(null);
  const signedIn = Boolean(getToken());

  const loadPage = useCallback(async (before) => {
    const url = new URL(`${BASE}/notes/timeline`);
    url.searchParams.set("limit", "30");
    if (before) url.searchParams.set("before", before);
    const r = await fetch(url.toString());
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await loadPage(null);
        if (!alive) return;
        setNotes(d.notes || []);
        setCursor(d.next_cursor || null);
      } catch (e) {
        if (alive) setError(e.message || "load_failed");
      }
    })();
    return () => { alive = false; };
  }, [loadPage]);

  const loadMore = useCallback(async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await loadPage(cursor);
      setNotes((prev) => [...(prev || []), ...(d.notes || [])]);
      setCursor(d.next_cursor || null);
    } catch (e) {
      setError(e.message || "load_more_failed");
    } finally {
      setLoadingMore(false);
    }
  }, [cursor, loadingMore, loadPage]);

  // IntersectionObserver auto-loads the next page when the sentinel is on-screen.
  useEffect(() => {
    if (!stopRef.current || !cursor) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) loadMore();
    }, { rootMargin: "300px" });
    io.observe(stopRef.current);
    return () => io.disconnect();
  }, [cursor, loadMore]);

  if (error && !notes) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center text-slate-500">
        Couldn't load Notes: {error}
      </div>
    );
  }
  if (notes === null) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading Notes…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-6 py-8" data-testid="publisher-notes-feed">
      {/* Header */}
      <header className="pb-5 border-b border-slate-200 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
          <StickyNote className="w-3.5 h-3.5" /> Notes · Publisher
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">
          Every short-form update, live
        </h1>
        <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
          Quick takes, market updates, and observations from writers across
          the SocialPulse Publisher — freshest at the top.
        </p>
      </header>

      {notes.length === 0 ? (
        <div
          className="text-center py-16 text-slate-500 italic"
          data-testid="publisher-notes-empty"
        >
          No notes yet — check back soon.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="publisher-notes-list">
          {notes.map((n) => (
            <li key={n.article_id}>
              <NoteRow n={n} />
            </li>
          ))}
        </ul>
      )}

      {/* Infinite-scroll sentinel */}
      {cursor && (
        <div ref={stopRef} className="py-8 text-center text-xs text-slate-400" data-testid="publisher-notes-loadmore">
          {loadingMore ? (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading more…
            </span>
          ) : (
            <button
              type="button"
              onClick={loadMore}
              className="text-blue-700 hover:underline"
              data-testid="publisher-notes-loadmore-button"
            >
              Load older notes
            </button>
          )}
        </div>
      )}

      {/* Floating "Post a note" FAB */}
      <button
        type="button"
        onClick={() => setComposerOpen(true)}
        className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/30 px-5 py-3 font-semibold text-sm transition-transform hover:-translate-y-0.5"
        data-testid="publisher-notes-fab"
        aria-label="Post a note"
      >
        <Plus className="w-4 h-4" />
        Post a note
      </button>

      {composerOpen && (
        <NoteComposer
          signedIn={signedIn}
          onClose={() => setComposerOpen(false)}
          onPosted={(newNote) => {
            setNotes((prev) => [newNote, ...(prev || [])]);
            setComposerOpen(false);
          }}
        />
      )}
    </div>
  );
}


function NoteComposer({ signedIn, onClose, onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("local");
  const [locationId, setLocationId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    // Focus the title on open. Autofocus is unreliable inside portals.
    const t = window.setTimeout(() => textareaRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, []);

  const canPost = title.trim().length >= 3 && body.trim().length >= 5 && !busy && signedIn;

  const post = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = getToken();
      // Wrap body lines into <p> tags — no HTML injection risk since we
      // strip on save via the backend Pydantic validators.
      const bodyHtml = body
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
        .join("");
      const createResp = await fetch(`${BASE}/articles`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          body_html: bodyHtml,
          primary_category: category,
          primary_location_id: locationId.trim() || null,
          editorial_label: "announcement",
          content_format: "note",
          original_language: "en",
        }),
      });
      if (!createResp.ok) throw new Error(`create HTTP ${createResp.status}`);
      const created = await createResp.json();
      const pubResp = await fetch(`${BASE}/articles/${created.id}/publish`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!pubResp.ok) throw new Error(`publish HTTP ${pubResp.status}`);

      // Optimistic push to the feed.
      onPosted({
        article_id: created.id,
        slug: created.slug,
        title: title.trim(),
        summary: null,
        body_html: bodyHtml,
        canonical_url: created.canonical_url,
        cover_image_url: null,
        primary_category: category,
        primary_location_id: locationId.trim() || null,
        editorial_label: "announcement",
        published_at: new Date().toISOString(),
        author: { id: "me", name: "You", handle: null },
      });
    } catch (e) {
      setError(e.message || "Failed to post note");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="note-composer-heading"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="publisher-notes-composer"
    >
      <div className="w-full sm:max-w-xl bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-amber-50/60">
          <h2 id="note-composer-heading" className="text-sm font-bold text-amber-900 inline-flex items-center gap-1.5">
            <StickyNote className="w-4 h-4" /> Post a note
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-amber-100 inline-flex items-center justify-center"
            aria-label="Close composer"
            data-testid="publisher-notes-composer-close"
          >
            <X className="w-4 h-4 text-amber-900" />
          </button>
        </header>

        {!signedIn ? (
          <div className="px-5 py-8 text-center">
            <LogIn className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-600 mb-4">
              Sign in to drop a note. It takes 15 seconds and shows up here immediately.
            </p>
            <a
              href="/login"
              className="inline-flex items-center gap-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2"
              data-testid="publisher-notes-composer-signin"
            >
              Sign in
            </a>
          </div>
        ) : (
          <div className="p-5 space-y-3">
            <input
              ref={textareaRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Headline (e.g. Onion prices up 12% in Pala today)"
              maxLength={200}
              className="w-full text-lg font-bold outline-none placeholder:text-slate-300 border-b border-slate-100 pb-2"
              data-testid="publisher-notes-composer-title"
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Say more — a paragraph or two is perfect. Blank lines start a new paragraph."
              rows={6}
              maxLength={4000}
              className="w-full text-sm text-slate-800 outline-none resize-none placeholder:text-slate-400"
              data-testid="publisher-notes-composer-body"
            />
            <div className="grid grid-cols-2 gap-2">
              <label className="flex flex-col text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                Category
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="mt-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white outline-none focus:border-blue-400 font-normal normal-case text-slate-900"
                  data-testid="publisher-notes-composer-category"
                >
                  {[
                    "local", "agriculture", "food", "business", "politics",
                    "sports", "technology", "health", "climate", "community",
                    "world",
                  ].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="flex flex-col text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                Location (optional)
                <input
                  type="text"
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                  placeholder="IN-KL-pala"
                  maxLength={64}
                  className="mt-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:border-blue-400 font-mono normal-case text-slate-900"
                  data-testid="publisher-notes-composer-location"
                />
              </label>
            </div>

            {error && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                {body.length}/4000
              </span>
              <button
                type="button"
                onClick={post}
                disabled={!canPost}
                className="inline-flex items-center gap-1 rounded-full bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white text-sm font-semibold px-4 py-2"
                data-testid="publisher-notes-composer-post"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Posting…
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" /> Post note
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}


function NoteRow({ n }) {
  const author = n.author || {};
  const authorHref = author.id ? `/publisher/author/${author.id}` : "#";
  return (
    <article
      className="bg-amber-50/60 border border-amber-200 rounded-2xl p-4 sm:p-5 hover:border-amber-300 transition-colors"
      data-testid={`publisher-note-${n.article_id}`}
    >
      {/* Author + meta */}
      <div className="flex items-center gap-2 text-xs text-slate-600 mb-2">
        <Link
          to={authorHref}
          className="inline-flex items-center gap-1 font-semibold text-slate-900 hover:text-blue-700"
          data-testid={`publisher-note-author-${n.article_id}`}
        >
          <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 inline-flex items-center justify-center text-[10px] font-bold uppercase">
            {(author.name || "?").charAt(0)}
          </span>
          {author.name || "Anonymous"}
        </Link>
        {author.handle && (
          <span className="text-slate-400">@{author.handle}</span>
        )}
        {n.published_at && (
          <>
            <span className="text-slate-300">·</span>
            <time dateTime={n.published_at} className="text-slate-500">
              {timeAgo(n.published_at)}
            </time>
          </>
        )}
      </div>

      {/* Category / location */}
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
        <span className="rounded-full bg-white border border-amber-200 px-2 py-0.5 text-amber-700">NOTE</span>
        <span className="text-blue-700">{n.primary_category}</span>
        {n.primary_location_id && (
          <>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-0.5">
              <MapPin className="w-2.5 h-2.5" /> {n.primary_location_id}
            </span>
          </>
        )}
      </div>

      {/* Title */}
      <h2
        className="text-lg sm:text-xl font-bold text-slate-900 leading-tight"
        data-testid={`publisher-note-title-${n.article_id}`}
      >
        {n.title}
      </h2>
      {n.summary && (
        <p className="text-sm text-slate-700 mt-1 leading-snug">{n.summary}</p>
      )}

      {/* Body */}
      {n.body_html && (
        <div
          className="mt-3 text-sm text-slate-800 leading-relaxed [&_p]:my-2 [&_a]:text-blue-700 [&_a]:underline [&_strong]:text-slate-900"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: n.body_html }}
          data-testid={`publisher-note-body-${n.article_id}`}
        />
      )}

      {/* Footer link */}
      <a
        href={n.canonical_url}
        target="_blank"
        rel="noopener"
        className="mt-3 inline-flex items-center gap-0.5 text-xs font-semibold text-amber-800 hover:text-amber-900"
      >
        Open on Publisher <ArrowUpRight className="w-3 h-3" />
      </a>
    </article>
  );
}
